import React, { useCallback, useMemo } from 'react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    LabelList,
} from 'recharts';
import { Card } from 'antd';
import { stageColors as defaultStageColors } from './Clicks/stageColors';
import { getCancellationDate, getQualitativeKPI, getStageActualMonth, getStageActualQuarter, getStageActualWeek, getStageActualYear, getStageOriginalPlanMonth, getStageOriginalPlanQuarter, getStageOriginalPlanWeek, getStageOriginalPlanYear, getStagePlanMonth, getStagePlanQuarter, getStagePlanWeek, getStagePlanYear, L0ActualDate, L1ActualDate, L1OriginalPlanDate, L2ActualDate, L2OriginalPlanDate, L2PlanDate, L3ActualDate, L3OriginalPlanDate, L3PlanDate, L4ActualDate, L4OriginalPlanDate, L4PlanDate, L5ActualDate, L5OriginalPlanDate, L5PlanDate, SubmittedForL1PlanDate, SubmittedForL2PlanDate, SubmittedForL3PlanDate, SubmittedForL4PlanDate, SubmittedForL5PlanDate } from '@/utils/Sort/checkDate/getDate';
import determineStage from '@/utils/DetermineStage';
import checkWorkstreamLeadApprovalStatus from '@/utils/Sort/checkWorkstreamLeadApprovalStatus';
import checkWorkstreamSponsorApprovalStatus from '@/utils/Sort/checkWorkstreamSponsorApprovalStatus';
import { checkTOFinanceApprovalStatus, getComplianceStatusForGrouping, getNetRecurringBenefitCatagory, getNetRecurringBenefitCatagoryL1Plus, getNetRecurringBenefitCatagoryL2Plus, getNetRecurringBenefitCatagoryL3Plus, getNetRecurringBenefitCatagoryL4Plus, LLatestActualDate } from '@/utils/helpers/helpers';
import checkStrategyAnalystApprovalStatus from '@/utils/Sort/checkStrategyAnalystApprovalStatus';
import InitiativePlanningSummary from '@/utils/initiativePlanningSummary';
import planningSummarySimplified from '@/utils/planningSummarySimplified';
import getImplementationCostCatagory from '@/utils/Sort/getImplementationCostCatagory';
import sortInitiativesByActualImpact from '@/utils/Sort/sortByActualImpact';
import sortInitiativesByActuals from '@/utils/Sort/sortByActual';
// Helper function to safely sum values
const sumAnnualizedValue = (annualizedValue) => {
    if (typeof annualizedValue === 'number') {
        return annualizedValue;
    }
    if (Array.isArray(annualizedValue)) {
        return annualizedValue.reduce((sum, value) => sum + (value || 0), 0);
    }
    return 0;
};
const CustomTotalLabel = (props) => {
    const { x, y, width, value } = props;
    if (value === 0) return null;
    return (
        <text x={x + width + 5} y={y + 10} fill="#000" fontSize={12} textAnchor="start">
            {`Total: ${parseFloat(value).toFixed(3)}`}
        </text>
    );
};
const OneTimeImplementationCostChart = ({
    initiatives,
    initiativeColors,
    colorByAttribute,
    chartHeight = 1000,
    handleClick,
    selectedGroup
}) => {
    const getGroupKey = useCallback((initiative) => {
        if (!selectedGroup || selectedGroup === 'None') {
            // Default to initiative name if no group is selected
            return initiative.workstreamSponsorWorkstream?.[0]?.workstreamName;
        }

        switch (selectedGroup) {
            case 'initiativeOwnerInfo':
            case 'workstreamLeadInfo':
            case 'workstreamSponsorInfo':
            case 'initiativeOwnerBackupInfo':
            case 'AllowOnlyTo':
                const person = initiative[selectedGroup]?.[0];
                return person ? `${person.firstName} ${person.lastName}` : 'Unknown';

            case 'workstreamSponsorWorkstream':
                return initiative[selectedGroup]?.[0]?.workstreamName || 'Unknown';

            case 'workstreamLeadSubworkstream':
                return initiative[selectedGroup]?.[0]?.subworkstreamName || 'Unknown';

            case 'stage':
                return determineStage(initiative);

            case 'Stage (simplified)':
                return initiative.stage;

            case 'selectedForToMeetingDescusion':
            case 'hasFinancialImpact':
                return initiative[selectedGroup] ? 'Yes' : 'No';

            case 'Has qualitative KPI?':
                return getQualitativeKPI(initiative);

            case 'initiativeNumber - initiativeName':
                return `${initiative.initiativeNumber} - ${initiative.initiativeName}`;

            case 'workstreamLeadPendingApproval':
                return checkWorkstreamLeadApprovalStatus(initiative);

            case 'workstreamSponsorPendingApproval':
                return checkWorkstreamSponsorApprovalStatus(initiative);

            case 'financeLeadPendingApproval':
                return checkTOFinanceApprovalStatus(initiative);

            case 'Strategy analyst pending approval?':
                return checkStrategyAnalystApprovalStatus(initiative);

            case 'planningSummary':
                return InitiativePlanningSummary(initiative).planningStatus;

            case 'Planning summary (simplified)':
                return planningSummarySimplified(initiative);

            case 'netRecurringBenefitsRange':
                return getNetRecurringBenefitCatagory(initiative);

            case 'Future implementation costs range':
                return getImplementationCostCatagory(initiative);
            case 'l2ForecastDate':
                return L2PlanDate(initiative);
            case 'L3 date compliance check (forecast vs planned date)':
                return getComplianceStatusForGrouping(initiative, 'L3');

            case 'L4 date compliance check (forecast vs planned date)':
                return getComplianceStatusForGrouping(initiative, 'L4');

            case 'Number of Attachment':
                return initiative.attachmentCount;

            case 'actualImpactUpdateCheck':
            case 'actualKpiUpdateCheck':
                return sortInitiativesByActualImpact(initiative, selectedGroup);

            case '(FY24-25) Actual impact update check':
            case '(FY23-24) Actual impact update check':
                return sortInitiativesByActuals(initiative, selectedGroup);

            case 'cancellationDate':
                return getCancellationDate(initiative);

            case 'l3PlannedDate':
                return L3PlanDate(initiative);
            case 'l4PlannedDate':
                return L4PlanDate(initiative);
            case 'l5LatestEstimatedDate':
            case 'l5ForecastDate':
                return L5PlanDate(initiative);
            case 'l0ActualDate':
                return L0ActualDate(initiative);
            case 'l1ActualDate':
                return L1ActualDate(initiative);
            case 'l2ActualDate':
                return L2ActualDate(initiative);
            case 'l3ActualDate':
                return L3ActualDate(initiative);
            case 'l4ActualDate':
                return L4ActualDate(initiative);
            case 'l5ActualDate':
                return L5ActualDate(initiative);
            case 'latestActualDateAllLGates':
                return LLatestActualDate(initiative);
            case 'subForL1ApprovDate':
                return SubmittedForL1PlanDate(initiative);
            case 'subForL2ApprovDate':
                return SubmittedForL2PlanDate(initiative);
            case 'subForL3ApprovDate':
                return SubmittedForL3PlanDate(initiative);
            case 'subForL4ApprovDate':
                return SubmittedForL4PlanDate(initiative);
            case 'subForL5ApprovDate':
                return SubmittedForL5PlanDate(initiative);
            case 'subForL5ApprovDate_Year':
                return getStageActualYear(initiative, 'Submitted for L5 Approval');
            case 'subForL5ApprovDate_Quarter':
                return getStageActualQuarter(initiative, 'Submitted for L5 Approval');
            case 'subForL5ApprovDate_Month':
                return getStageActualMonth(initiative, 'Submitted for L5 Approval');
            case 'subForL5ApprovDate_Week':
                return getStageActualWeek(initiative, 'Submitted for L5 Approval');
            case 'pipelineWeeklyVarianceL0Plus':
                return getNetRecurringBenefitCatagoryL0Plus(initiative);
            case 'pipelineWeeklyVarianceL1Plus':
                return getNetRecurringBenefitCatagoryL1Plus(initiative);
            case 'pipelineWeeklyVarianceL2Plus':
                return getNetRecurringBenefitCatagoryL2Plus(initiative);
            case 'pipelineWeeklyVarianceL3Plus':
                return getNetRecurringBenefitCatagoryL3Plus(initiative);
            case 'pipelineWeeklyVarianceL4Plus':
                return getNetRecurringBenefitCatagoryL4Plus(initiative);
            default:
                if (selectedGroup.startsWith('l0ActualDate')) {
                    if (selectedGroup.endsWith('_Day')) {
                        return L0ActualDate(initiative);
                    } else if (selectedGroup.endsWith('_Year')) {
                        return getStageActualYear(initiative, 'L0');
                    }
                    else if (selectedGroup.endsWith('_Quarter')) {
                        return getStageActualQuarter(initiative, 'L0');
                    }
                    else if (selectedGroup.endsWith('_Month')) {
                        return getStageActualMonth(initiative, 'L0');
                    }
                    else if (selectedGroup.endsWith('_Week')) {
                        return getStageActualWeek(initiative, 'L0');
                    }
                }
                else if (selectedGroup.startsWith('l5LatestEstimatedDate') || selectedGroup.startsWith('l5ForecastDate')) {
                    if (selectedGroup.endsWith('_Day')) {
                        return L5PlanDate(initiative);
                    } else if (selectedGroup.endsWith('_Year')) {
                        return getStagePlanYear(initiative, 'L5');
                    }
                    else if (selectedGroup.endsWith('_Quarter')) {
                        return getStagePlanQuarter(initiative, 'L5');
                    }
                    else if (selectedGroup.endsWith('_Month')) {
                        return getStagePlanMonth(initiative, 'L5');
                    }
                    else if (selectedGroup.endsWith('_Week')) {
                        return getStagePlanWeek(initiative, 'L5');
                    }
                }
                else if (selectedGroup.startsWith('l4PlannedDate')) {
                    if (selectedGroup.endsWith('_Day')) {
                        return L4PlanDate(initiative);
                    } else if (selectedGroup.endsWith('_Year')) {
                        return getStagePlanYear(initiative, 'L4');
                    }
                    else if (selectedGroup.endsWith('_Quarter')) {
                        return getStagePlanQuarter(initiative, 'L4');
                    }
                    else if (selectedGroup.endsWith('_Month')) {
                        return getStagePlanMonth(initiative, 'L4');
                    }
                    else if (selectedGroup.endsWith('_Week')) {
                        return getStagePlanWeek(initiative, 'L4');
                    }
                }
                else if (selectedGroup.startsWith('l3PlannedDate')) {
                    if (selectedGroup.endsWith('_Day')) {
                        return L3PlanDate(initiative);
                    } else if (selectedGroup.endsWith('_Year')) {
                        return getStagePlanYear(initiative, 'L3');
                    }
                    else if (selectedGroup.endsWith('_Quarter')) {
                        return getStagePlanQuarter(initiative, 'L3');
                    }
                    else if (selectedGroup.endsWith('_Month')) {
                        return getStagePlanMonth(initiative, 'L3');
                    }
                    else if (selectedGroup.endsWith('_Week')) {
                        return getStagePlanWeek(initiative, 'L3');
                    }
                }
                else if (selectedGroup.startsWith('l2ForecastDate')) {
                    if (selectedGroup.endsWith('_Day')) {
                        return L2PlanDate(initiative);
                    } else if (selectedGroup.endsWith('_Year')) {
                        return getStagePlanYear(initiative, 'L2');
                    }
                    else if (selectedGroup.endsWith('_Quarter')) {
                        return getStagePlanQuarter(initiative, 'L2');
                    }
                    else if (selectedGroup.endsWith('_Month')) {
                        return getStagePlanMonth(initiative, 'L2');
                    }
                    else if (selectedGroup.endsWith('_Week')) {
                        return getStagePlanWeek(initiative, 'L2');
                    }
                }
                else if (selectedGroup.startsWith('l1ActualDate')) {
                    if (selectedGroup.endsWith('_Day')) {
                        return L1ActualDate(initiative);
                    } else if (selectedGroup.endsWith('_Year')) {
                        return getStageActualYear(initiative, 'L1');
                    }
                    else if (selectedGroup.endsWith('_Quarter')) {
                        return getStageActualQuarter(initiative, 'L1');
                    }
                    else if (selectedGroup.endsWith('_Month')) {
                        return getStageActualMonth(initiative, 'L1');
                    }
                    else if (selectedGroup.endsWith('_Week')) {
                        return getStageActualWeek(initiative, 'L1');
                    }
                }
                else if (selectedGroup.startsWith('l2ActualDate')) {
                    if (selectedGroup.endsWith('_Day')) {
                        return L2ActualDate(initiative);
                    } else if (selectedGroup.endsWith('_Year')) {
                        return getStageActualYear(initiative, 'L2');
                    }
                    else if (selectedGroup.endsWith('_Quarter')) {
                        return getStageActualQuarter(initiative, 'L2');
                    }
                    else if (selectedGroup.endsWith('_Month')) {
                        return getStageActualMonth(initiative, 'L2');
                    }
                    else if (selectedGroup.endsWith('_Week')) {
                        return getStageActualWeek(initiative, 'L2');
                    }
                }
                else if (selectedGroup.startsWith('l3ActualDate')) {
                    if (selectedGroup.endsWith('_Day')) {
                        return L3ActualDate(initiative);
                    } else if (selectedGroup.endsWith('_Year')) {
                        return getStageActualYear(initiative, 'L3');
                    }
                    else if (selectedGroup.endsWith('_Quarter')) {
                        return getStageActualQuarter(initiative, 'L3');
                    }
                    else if (selectedGroup.endsWith('_Month')) {
                        return getStageActualMonth(initiative, 'L3');
                    }
                    else if (selectedGroup.endsWith('_Week')) {
                        return getStageActualWeek(initiative, 'L3');
                    }
                }
                else if (selectedGroup.startsWith('latestActualDateAllLGates')) {
                    if (selectedGroup.endsWith('_Day')) {
                        return LLatestActualDate(initiative);
                    } else if (selectedGroup.endsWith('_Year')) {
                        return getStageActualYear(initiative, 'AllLGates');
                    }
                    else if (selectedGroup.endsWith('_Quarter')) {
                        return getStageActualQuarter(initiative, 'AllLGates');
                    }
                    else if (selectedGroup.endsWith('_Month')) {
                        return getStageActualMonth(initiative, 'AllLGates');
                    }
                    else if (selectedGroup.endsWith('_Week')) {
                        return getStageActualWeek(initiative, 'AllLGates');
                    }
                }
                else if (selectedGroup.startsWith('l4ActualDate')) {
                    if (selectedGroup.endsWith('_Day')) {
                        return L4ActualDate(initiative);
                    } else if (selectedGroup.endsWith('_Year')) {
                        return getStageActualYear(initiative, 'L4');
                    }
                    else if (selectedGroup.endsWith('_Quarter')) {
                        return getStageActualQuarter(initiative, 'L4');
                    }
                    else if (selectedGroup.endsWith('_Month')) {
                        return getStageActualMonth(initiative, 'L4');
                    }
                    else if (selectedGroup.endsWith('_Week')) {
                        return getStageActualWeek(initiative, 'L4');
                    }
                }
                else if (selectedGroup?.startsWith('L1 Original Planned Date')) {
                    if (selectedGroup?.endsWith('_Day')) {
                        return L1OriginalPlanDate(initiative);
                    } else if (selectedGroup?.endsWith('_Year')) {
                        return getStageOriginalPlanYear(initiative, 'L1');
                    }
                    else if (selectedGroup?.endsWith('_Quarter')) {
                        return getStageOriginalPlanQuarter(initiative, 'L1');
                    }
                    else if (selectedGroup?.endsWith('_Month')) {
                        return getStageOriginalPlanMonth(initiative, 'L1');
                    }
                    else if (selectedGroup?.endsWith('_Week')) {
                        return getStageOriginalPlanWeek(initiative, 'L1');
                    }
                }
                else if (selectedGroup?.startsWith('L2 Original Planned Date')) {
                    if (selectedGroup?.endsWith('_Day')) {
                        return L2OriginalPlanDate(initiative);
                    } else if (selectedGroup?.endsWith('_Year')) {
                        return getStageOriginalPlanYear(initiative, 'L2');
                    }
                    else if (selectedGroup?.endsWith('_Quarter')) {
                        return getStageOriginalPlanQuarter(initiative, 'L2');
                    }
                    else if (selectedGroup?.endsWith('_Month')) {
                        return getStageOriginalPlanMonth(initiative, 'L2');
                    }
                    else if (selectedGroup?.endsWith('_Week')) {
                        return getStageOriginalPlanWeek(initiative, 'L2');
                    }
                }
                else if (selectedGroup?.startsWith('L3 Original Planned Date')) {
                    if (selectedGroup?.endsWith('_Day')) {
                        return L3OriginalPlanDate(initiative);
                    } else if (selectedGroup?.endsWith('_Year')) {
                        return getStageOriginalPlanYear(initiative, 'L3');
                    }
                    else if (selectedGroup?.endsWith('_Quarter')) {
                        return getStageOriginalPlanQuarter(initiative, 'L3');
                    }
                    else if (selectedGroup?.endsWith('_Month')) {
                        return getStageOriginalPlanMonth(initiative, 'L3');
                    }
                    else if (selectedGroup?.endsWith('_Week')) {
                        return getStageOriginalPlanWeek(initiative, 'L3');
                    }
                }
                else if (selectedGroup?.startsWith('L4 Original Planned Date')) {
                    if (selectedGroup?.endsWith('_Day')) {
                        return L4OriginalPlanDate(initiative);
                    } else if (selectedGroup?.endsWith('_Year')) {
                        return getStageOriginalPlanYear(initiative, 'L4');
                    }
                    else if (selectedGroup?.endsWith('_Quarter')) {
                        return getStageOriginalPlanQuarter(initiative, 'L4');
                    }
                    else if (selectedGroup?.endsWith('_Month')) {
                        return getStageOriginalPlanMonth(initiative, 'L4');
                    }
                    else if (selectedGroup?.endsWith('_Week')) {
                        return getStageOriginalPlanWeek(initiative, 'L4');
                    }
                }
                else if (selectedGroup?.startsWith('L5 Original Planned Date')) {
                    if (selectedGroup?.endsWith('_Day')) {
                        return L5OriginalPlanDate(initiative);
                    } else if (selectedGroup?.endsWith('_Year')) {
                        return getStageOriginalPlanYear(initiative, 'L5');
                    }
                    else if (selectedGroup?.endsWith('_Quarter')) {
                        return getStageOriginalPlanQuarter(initiative, 'L5');
                    }
                    else if (selectedGroup?.endsWith('_Month')) {
                        return getStageOriginalPlanMonth(initiative, 'L5');
                    }
                    else if (selectedGroup?.endsWith('_Week')) {
                        return getStageOriginalPlanWeek(initiative, 'L5');
                    }
                }
                else if (selectedGroup.startsWith('l5ActualDate')) {
                    if (selectedGroup.endsWith('_Day')) {
                        return L5ActualDate(initiative);
                    } else if (selectedGroup.endsWith('_Year')) {
                        return getStageActualYear(initiative, 'L5');
                    }
                    else if (selectedGroup.endsWith('_Quarter')) {
                        return getStageActualQuarter(initiative, 'L5');
                    }
                    else if (selectedGroup.endsWith('_Month')) {
                        return getStageActualMonth(initiative, 'L5');
                    }
                    else if (selectedGroup.endsWith('_Week')) {
                        return getStageActualWeek(initiative, 'L5');
                    }
                }
                else if (selectedGroup.startsWith('subForL1ApprovDate')) {
                    if (selectedGroup.endsWith('_Day')) {
                        return SubmittedForL1PlanDate(initiative);
                    } else if (selectedGroup.endsWith('_Year')) {
                        return getStageActualYear(initiative, 'Submitted for L1 Approval');
                    }
                    else if (selectedGroup.endsWith('_Quarter')) {
                        return getStageActualQuarter(initiative, 'Submitted for L1 Approval');
                    }
                    else if (selectedGroup.endsWith('_Month')) {
                        return getStageActualMonth(initiative, 'Submitted for L1 Approval');
                    }
                    else if (selectedGroup.endsWith('_Week')) {
                        return getStageActualWeek(initiative, 'Submitted for L1 Approval');
                    }
                }
                else if (selectedGroup.startsWith('subForL2ApprovDate')) {
                    if (selectedGroup.endsWith('_Day')) {
                        return SubmittedForL2PlanDate(initiative);
                    } else if (selectedGroup.endsWith('_Year')) {
                        return getStageActualYear(initiative, 'Submitted for L2 Approval');
                    }
                    else if (selectedGroup.endsWith('_Quarter')) {
                        return getStageActualQuarter(initiative, 'Submitted for L2 Approval');
                    }
                    else if (selectedGroup.endsWith('_Month')) {
                        return getStageActualMonth(initiative, 'Submitted for L2 Approval');
                    }
                    else if (selectedGroup.endsWith('_Week')) {
                        return getStageActualWeek(initiative, 'Submitted for L2 Approval');
                    }
                }
                else if (selectedGroup.startsWith('subForL3ApprovDate')) {
                    if (selectedGroup.endsWith('_Day')) {
                        return SubmittedForL3PlanDate(initiative);
                    } else if (selectedGroup.endsWith('_Year')) {
                        return getStageActualYear(initiative, 'Submitted for L3 Approval');
                    }
                    else if (selectedGroup.endsWith('_Quarter')) {
                        return getStageActualQuarter(initiative, 'Submitted for L3 Approval');
                    }
                    else if (selectedGroup.endsWith('_Month')) {
                        return getStageActualMonth(initiative, 'Submitted for L3 Approval');
                    }
                    else if (selectedGroup.endsWith('_Week')) {
                        return getStageActualWeek(initiative, 'Submitted for L3 Approval');
                    }
                }
                else if (selectedGroup.startsWith('subForL4ApprovDate')) {
                    if (selectedGroup.endsWith('_Day')) {
                        return SubmittedForL4PlanDate(initiative);
                    } else if (selectedGroup.endsWith('_Year')) {
                        return getStageActualYear(initiative, 'Submitted for L4 Approval');
                    }
                    else if (selectedGroup.endsWith('_Quarter')) {
                        return getStageActualQuarter(initiative, 'Submitted for L4 Approval');
                    }
                    else if (selectedGroup.endsWith('_Month')) {
                        return getStageActualMonth(initiative, 'Submitted for L4 Approval');
                    }
                    else if (selectedGroup.endsWith('_Week')) {
                        return getStageActualWeek(initiative, 'Submitted for L4 Approval');
                    }
                }
                else if (selectedGroup.startsWith('subForL5ApprovDate')) {
                    if (selectedGroup.endsWith('_Day')) {
                        return SubmittedForL5PlanDate(initiative);
                    } else if (selectedGroup.endsWith('_Year')) {
                        return getStageActualYear(initiative, 'Submitted for L5 Approval');
                    }
                    else if (selectedGroup.endsWith('_Quarter')) {
                        return getStageActualQuarter(initiative, 'Submitted for L5 Approval');
                    }
                    else if (selectedGroup.endsWith('_Month')) {
                        return getStageActualMonth(initiative, 'Submitted for L5 Approval');
                    }
                    else if (selectedGroup.endsWith('_Week')) {
                        return getStageActualWeek(initiative, 'Submitted for L5 Approval');
                    }
                }
                else if (selectedGroup.startsWith('cancellationDate')) {
                    if (selectedGroup?.endsWith('_Day')) {
                        return getCancellationDate(initiative);
                    } else if (selectedGroup?.endsWith('_Year')) {
                        return getCancellationYear(initiative);
                    }
                    else if (selectedGroup?.endsWith('_Quarter')) {
                        return getCancellationQuarter(initiative);
                    }
                    else if (selectedGroup?.endsWith('_Month')) {
                        return getCancellationMonth(initiative);
                    }
                    else if (selectedGroup?.endsWith('_Week')) {
                        return getCancellationWeek(initiative);
                    }
                }
                else {
                    // Handle any other group selection by directly accessing the property
                    return initiative[selectedGroup] || 'Unknown';
                }
        }
    }, [selectedGroup]);
    const allStages = useMemo(() =>
        [...new Set(initiatives.map(i => i.stage))].sort(),
        [initiatives]
    );
    const allGroupKeys = useMemo(() => {
        const keys = new Set();
        initiatives.forEach(initiative => {
            if (!['On Hold', 'Cancelled', 'Rejected'].includes(initiative.weeklyStatus)) {
                const key = getGroupKey(initiative);
                if (key) keys.add(key);
            }
        });
        return Array.from(keys).sort();
    }, [initiatives, getGroupKey]);
    const chartData = useMemo(() => {
        const oneTimeCostsData = initiatives.reduce((acc, initiative) => {
            if (['On Hold', 'Cancelled', 'Rejected'].includes(initiative.weeklyStatus)) {
                return acc;
            }

            const groupKey = getGroupKey(initiative);
            const stage = initiative.stage;

            if (!groupKey) return acc;
            if (!acc[groupKey]) {
                acc[groupKey] = { total: 0 };
                // Initialize all stages for this group
                allStages.forEach(s => {
                    acc[groupKey][s] = 0;
                });
            }

            let totalCostsForInitiative = 0;

            // Aggregate costs from Impact data
            const impacts = initiative.impact || [];
            impacts.forEach(impact => {
                if (impact.frequencyAndDirection === 'One Time implementation Cost (ETB)') {
                    const costValue = sumAnnualizedValue(impact.annualizedValues?.plan);
                    totalCostsForInitiative -= costValue; // Costs are negative benefits
                }
            });

            // Aggregate costs from KPI data
            const kpis = initiative.KPI || [];
            kpis.forEach(kpi => {
                const isRelevantKpi = kpi.UniversalKPI === 'One Time Implementation Cost for Amortization' ||
                    kpi.UniversalKPI === 'One Time Implementation Cost for Depreciation';
                if (isRelevantKpi) {
                    const kpiValue = (kpi.annuallyPlan || []).reduce((sum, val) => sum + (val || 0), 0);
                    totalCostsForInitiative -= kpiValue; // Costs are negative benefits
                }
            });

            acc[groupKey][stage] += totalCostsForInitiative;
            acc[groupKey].total += totalCostsForInitiative;
            return acc;
        }, {});

        return Object.keys(oneTimeCostsData)
            .sort((a, b) => oneTimeCostsData[b].total - oneTimeCostsData[a].total)
            .map(groupKey => {
                const groupData = oneTimeCostsData[groupKey];
                const newEntry = { groupKey };
                allStages.forEach(stage => {
                    const value = groupData[stage] || 0;
                    if (value >= 0) {
                        newEntry[`${stage}_Positive`] = value;
                    } else {
                        newEntry[`${stage}_Negative`] = value;
                    }
                });
                newEntry.originalTotal = groupData.total;
                return newEntry;
            });
    }, [initiatives, getGroupKey, allStages]);
    const minX = chartData.length > 0 ? Math.min(0, ...chartData.map(d => d.originalTotal)) : 0;
    const maxX = chartData.length > 0 ? Math.max(0, ...chartData.map(d => d.originalTotal)) : 0;
    // Updated chart title
    const chartTitle = selectedGroup && selectedGroup !== 'None'
        ? `One Time Implementation Costs by ${selectedGroup} (Planned, by stage, ETB Mn)`
        : "One Time Implementation Costs (Planned, by stage, ETB Mn)";
    const CustomTooltip = ({ active, payload, label }) => {
        if (!active || !payload || !payload.length) return null;
        const groupData = chartData.find(d => d.groupKey === label);
        if (!groupData) return null;
        const tooltipData = allStages.map(stage => {
            const positiveValue = groupData[`${stage}_Positive`] || 0;
            const negativeValue = groupData[`${stage}_Negative`] || 0;
            const totalValue = positiveValue + negativeValue;
            return {
                stage,
                positiveValue,
                negativeValue,
                totalValue
            };
        }).filter(item => item.totalValue !== 0);
        return (
            <div className="custom-tooltip" style={{
                backgroundColor: '#fff',
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                minWidth: '200px'
            }}>
                <p style={{ margin: 0, fontWeight: 'bold', marginBottom: '8px' }}>{label}</p>
                {tooltipData.map((item, index) => (
                    <div key={index} style={{
                        marginBottom: '4px',
                        display: 'flex',
                        justifyContent: 'space-between'
                    }}>
                        <span style={{ marginRight: '10px' }}>{item.stage}:</span>
                        <span>{parseFloat(item.totalValue).toFixed(3)} ETB Mn</span>
                    </div>
                ))}
                <div style={{
                    marginTop: '8px',
                    paddingTop: '8px',
                    borderTop: '1px solid #eee',
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontWeight: 'bold'
                }}>
                    <span>Total:</span>
                    <span>{parseFloat(groupData.originalTotal).toFixed(3)} ETB Mn</span>
                </div>
            </div>
        );
    };
    const colors = useMemo(() => {
        if (colorByAttribute === 'Stage (simplified)') {
            return initiativeColors;
        }
        return defaultStageColors;
    }, [colorByAttribute, initiativeColors]);
    const renderCustomLegend = (props) => {
        const { payload } = props;
        const uniqueStages = [...new Set(payload.map(entry => entry.value.replace('_Positive', '').replace('_Negative', '')))];
        return (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {uniqueStages.map((stage, index) => {
                    const entry = payload.find(p => p.value.startsWith(stage));
                    const color = entry ? (colors[stage] || entry.color) : '#ccc';
                    return (
                        <li
                            key={`item-${index}`}
                            style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}
                        >
                            <div
                                style={{
                                    width: '10px',
                                    height: '10px',
                                    backgroundColor: color,
                                    marginRight: '5px',
                                    display: 'inline-block',
                                }}
                            />
                            <span style={{ fontSize: '12px' }}>{stage}</span>
                        </li>
                    );
                })}
            </ul>
        );
    };
    return (
        <Card
            title={<span style={{ color: '#0078D4' }} onClick={handleClick}>{chartTitle}</span>}
            style={{ width: '100%', cursor: 'pointer' }}
        >
            <div style={{ width: '100%', height: chartHeight }}>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={chartData}
                        layout="vertical"
                        barCategoryGap="0%"
                        margin={{
                            top: 10,
                            right: 150,
                            left: 10,
                            bottom: 10
                        }}
                    >
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                        <XAxis
                            type="number"
                            domain={[minX * 1.1, maxX * 1.1]}
                            tickFormatter={(value) => parseFloat(value).toFixed(3)}
                            allowDecimals={false}
                        />
                        <YAxis
                            type="category"
                            dataKey="groupKey"
                            interval={0}
                            axisLine={false}
                            tickLine={false}
                            width={120}
                            tick={{ fill: '#000', fontSize: 12, textAnchor: 'end' }}
                            allowDataOverflow={true}
                        />
                        <Tooltip
                            content={<CustomTooltip />}
                            cursor={{ fill: 'rgba(0, 0, 0, 0.1)' }}
                        />
                        <Legend
                            layout="vertical"
                            verticalAlign="middle"
                            align="right"
                            content={renderCustomLegend}
                        />
                        {allStages.flatMap(stage => [
                            <Bar
                                key={`${stage}_Positive`}
                                dataKey={`${stage}_Positive`}
                                stackId="positive"
                                fill={colors[stage]}
                                isAnimationActive={false}
                                name={stage}
                            >
                                <LabelList
                                    dataKey={`${stage}_Positive`}
                                    position="insideRight"
                                    formatter={(value) => value !== 0 ? parseFloat(value).toFixed(3) : ''}
                                    fill="#fff"
                                    fontSize={10}
                                />
                            </Bar>,
                            <Bar
                                key={`${stage}_Negative`}
                                dataKey={`${stage}_Negative`}
                                stackId="negative"
                                fill={colors[stage]}
                                isAnimationActive={false}
                                name={stage}
                                hide={true}
                            >
                                <LabelList
                                    dataKey={`${stage}_Negative`}
                                    position="insideLeft"
                                    formatter={(value) => value !== 0 ? parseFloat(value).toFixed(3) : ''}
                                    fill="#fff"
                                    fontSize={10}
                                />
                            </Bar>,
                        ])}
                        <LabelList dataKey="originalTotal" content={<CustomTotalLabel data={chartData} />} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </Card>
    );
};

export default OneTimeImplementationCostChart;